import { useState } from 'react';
import { App, Checkbox, Input, InputNumber, Modal, Select } from 'antd';
import type { MockServerRoute } from '@core/entities/MockServer';
import type { MockRoutePayload } from '@core/repositories/MockServerRepository';

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'].map((m) => ({
  label: m,
  value: m,
}));

const headersToText = (headers: Record<string, string> | null): string =>
  headers
    ? Object.entries(headers)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n')
    : '';

const textToHeaders = (text: string): Record<string, string> | null => {
  const entries = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line): [string, string] => {
      const index = line.indexOf(':');
      return index === -1
        ? [line, '']
        : [line.slice(0, index).trim(), line.slice(index + 1).trim()];
    })
    .filter(([key]) => key !== '');

  return entries.length === 0 ? null : Object.fromEntries(entries);
};

interface MockRouteFormModalProps {
  open: boolean;
  route: MockServerRoute | null;
  confirmLoading?: boolean;
  onSubmit: (payload: MockRoutePayload) => void;
  onCancel: () => void;
}

export const MockRouteFormModal = ({
  open,
  route,
  confirmLoading,
  onSubmit,
  onCancel,
}: MockRouteFormModalProps) => {
  const { message } = App.useApp();
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('');
  const [statusCode, setStatusCode] = useState(200);
  const [headersText, setHeadersText] = useState('');
  const [body, setBody] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setMethod(route?.method ?? 'GET');
      setPath(route?.path ?? '');
      setStatusCode(route?.statusCode ?? 200);
      setHeadersText(headersToText(route?.responseHeaders ?? null));
      setBody(route?.responseBody ?? '');
      setIsEnabled(route?.isEnabled ?? true);
    }
  }

  const handleSubmit = () => {
    if (!path.trim()) {
      void message.error('Enter a path first');
      return;
    }

    onSubmit({
      method,
      path: path.trim(),
      statusCode,
      responseHeaders: textToHeaders(headersText),
      responseBody: body,
      isEnabled,
    });
  };

  return (
    <Modal
      open={open}
      title={route ? 'Edit route' : 'Add route'}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={confirmLoading}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Select value={method} onChange={setMethod} options={METHOD_OPTIONS} className="w-28" />
          <Input
            value={path}
            onChange={(event) => setPath(event.target.value)}
            placeholder="/users/:id"
            className="flex-1 font-mono"
          />
          <InputNumber
            value={statusCode}
            onChange={(value) => setStatusCode(value ?? 200)}
            min={100}
            max={599}
            className="w-24"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-500">
            Response headers (one per line, key: value)
          </label>
          <Input.TextArea
            value={headersText}
            onChange={(event) => setHeadersText(event.target.value)}
            placeholder={'Content-Type: application/json'}
            autoSize={{ minRows: 2, maxRows: 6 }}
            className="font-mono text-xs"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-500">Response body</label>
          <Input.TextArea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={'{"ok":true}'}
            autoSize={{ minRows: 4, maxRows: 12 }}
            className="font-mono text-xs"
          />
        </div>

        <Checkbox checked={isEnabled} onChange={(event) => setIsEnabled(event.target.checked)}>
          Enabled
        </Checkbox>
      </div>
    </Modal>
  );
};
